import { c as _c } from "react/compiler-runtime";
import { useState, type KeyboardEvent, type RefObject } from "react";
import { match, P } from "ts-pattern";
import { z } from "zod";
import { safeLocalStorage } from "@/lib/storage";
const HISTORY_LIMIT = 50;
const STORAGE_KEY_PREFIX = "blacksmith_command_palette_history_";
const storedEntriesSchema = z.array(z.string());
type RecalledEntry = {
  storageKey: string | null;
  index: number;
  text: string;
};
type HistoryPosition = {
  entries: string[];
  index: number | null;
};
type CommandPaletteHistory = {
  handleArrowKey: (event: KeyboardEvent<HTMLDivElement>) => void;
  remember: (entry: string) => void;
  rememberDismissedSearch: () => void;
};
function historyStorageKey(userId: number | undefined, org: string | null | undefined): string | null {
  if (userId === undefined || !org) return null;
  return `${STORAGE_KEY_PREFIX}${userId}_${org.toLowerCase()}`; // URL org casing follows whichever link was clicked
}
function readEntries(storageKey: string | null): string[] {
  if (storageKey === null) return [];
  const raw = safeLocalStorage.get(storageKey);
  if (raw === null) return [];
  try {
    const parsed = storedEntriesSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : [];
  } catch {
    return [];
  }
}

/** `["jobs", "Logs"]` + `"logs"` → `["logs", "jobs"]` */
function withEntryFirst(entries: string[], entry: string): string[] {
  const dedupeKey = entry.toLowerCase();
  const others = entries.filter(existing => existing.toLowerCase() !== dedupeKey);
  return [entry, ...others].slice(0, HISTORY_LIMIT);
}
function indexOfEntry(entries: string[], text: string): number | null {
  const key = text.toLowerCase();
  const index = entries.findIndex(entry => entry.toLowerCase() === key);
  return index === -1 ? null : index;
}
function isTopRowHighlighted(root: HTMLElement | null): boolean {
  const topRow = root?.querySelector('[cmdk-item]:not([aria-disabled="true"])');
  if (!topRow) return true;
  return topRow.getAttribute("aria-selected") === "true";
}

/** Typed text counts as the newest entry: `"dep"` + `["Logs"]` → `["dep", "Logs"]` at 0 */
function historyPosition(stored: string[], browsingIndex: number | null, search: string): HistoryPosition {
  const draft = search.trim();
  return match({
    browsingIndex,
    draft
  }).with({
    browsingIndex: P.number
  }, ({
    browsingIndex: index
  }) => ({
    entries: stored,
    index: indexOfEntry(stored, draft) ?? index // another tab may have saved since the recall, shifting positions
  })).with({
    draft: ""
  }, () => ({
    entries: stored,
    index: null
  })).otherwise(() => ({
    entries: withEntryFirst(stored, draft),
    index: 0
  }));
}
function olderEntryIndex({
  entries,
  index
}: HistoryPosition): number | null {
  const older = index === null ? 0 : index + 1;
  return older < entries.length ? older : null;
}

/**
 * Recall of past palette entries: ↑ on the top row steps back through older
 * entries, saving any typed text first. ↓ and every other row move through
 * results.
 */
export function useCommandPaletteHistory(t0) {
  const $ = _c(20);
  const {
    userId,
    org,
    search,
    setSearch,
    commandRef
  } = t0;
  let t1;
  if ($[0] !== org || $[1] !== userId) {
    t1 = historyStorageKey(userId, org);
    $[0] = org;
    $[1] = userId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const storageKey = t1;
  const [recalled, setRecalled] = useState(null);
  const isBrowsing = recalled !== null && recalled.text === search && recalled.storageKey === storageKey;
  if (recalled !== null && !isBrowsing) {
    setRecalled(null);
  }
  const browsingIndex = isBrowsing ? recalled.index : null;
  let t2;
  if ($[3] !== storageKey) {
    t2 = entry => {
      const trimmed = entry.trim();
      if (storageKey === null || trimmed === "") {
        return;
      }
      const entries = withEntryFirst(readEntries(storageKey), trimmed);
      safeLocalStorage.set(storageKey, JSON.stringify(entries));
    };
    $[3] = storageKey;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const remember = t2;
  let t3;
  if ($[5] !== browsingIndex || $[6] !== remember || $[7] !== search) {
    t3 = () => {
      if (browsingIndex !== null) {
        return;
      }
      remember(search);
    };
    $[5] = browsingIndex;
    $[6] = remember;
    $[7] = search;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const rememberDismissedSearch = t3;
  let t4;
  if ($[9] !== browsingIndex || $[10] !== commandRef || $[11] !== rememberDismissedSearch || $[12] !== search || $[13] !== setSearch || $[14] !== storageKey) {
    t4 = event => {
      if (event.nativeEvent.isComposing) {
        return;
      }
      if (event.key !== "ArrowUp") {
        return;
      }
      if (!isTopRowHighlighted(commandRef.current)) {
        return;
      }
      const position = historyPosition(readEntries(storageKey), browsingIndex, search);
      const index = olderEntryIndex(position);
      if (index === null) {
        return;
      }
      event.preventDefault();
      rememberDismissedSearch();
      const text = position.entries[index];
      setRecalled({
        storageKey,
        index,
        text
      });
      setSearch(text);
    };
    $[9] = browsingIndex;
    $[10] = commandRef;
    $[11] = rememberDismissedSearch;
    $[12] = search;
    $[13] = setSearch;
    $[14] = storageKey;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const handleArrowKey = t4;
  let t5;
  if ($[16] !== handleArrowKey || $[17] !== remember || $[18] !== rememberDismissedSearch) {
    t5 = {
      handleArrowKey,
      remember,
      rememberDismissedSearch
    };
    $[16] = handleArrowKey;
    $[17] = remember;
    $[18] = rememberDismissedSearch;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  return t5;
}